import React from 'react';
import { useSelector } from 'react-redux';
import { FaGift, FaCalendarAlt, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const UserBonus = () => {
  const { user } = useSelector((state) => state.auth);

  if (!user) {
    return null;
  }

  const bonus = user.bonus || {};
  
  // They have a valid active or expired bonus
  const hasValidBonus = user.hasReceivedFirstInvestmentBonus && bonus.amount > 0;
  
  // They are an old investor who was marked as having received it but given 0 amount
  const isOldIneligibleInvestor = user.hasReceivedFirstInvestmentBonus && (!bonus.amount || bonus.amount === 0);
  
  const now = new Date();
  const expiryDate = bonus.expiresAt ? new Date(bonus.expiresAt) : null;
  const isExpired = expiryDate ? now > expiryDate : false;
  
  return (
    <>
      <Helmet>
        <title>My Bonus - Assets Dairy Farm</title>
      </Helmet>
      
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">My Bonus</h1>
            <p className="text-gray-600 mt-1">View your first-time investment bonus</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 sm:p-10 text-center relative overflow-hidden">
            {isOldIneligibleInvestor ? (
              <div className="py-12">
                <FaInfoCircle className="text-gray-400 text-6xl mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-700">Not Eligible</h2>
                <p className="text-gray-500 mt-2 max-w-md mx-auto">
                  The First-Time Investment Bonus is a new promotional offer and is only applicable to new users making their very first investment.
                </p>
              </div>
            ) : !hasValidBonus ? (
              <div className="py-12">
                <FaGift className="text-gray-300 text-6xl mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-700">No Bonus Yet</h2>
                <p className="text-gray-500 mt-2 max-w-md mx-auto">
                  You will receive a 25% bonus on your first investment once it is approved by the admin.
                </p>
              </div>
            ) : isExpired ? (
              <div className="py-12">
                <FaExclamationTriangle className="text-orange-400 text-6xl mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-700">Bonus Expired</h2>
                <p className="text-gray-500 mt-2 max-w-md mx-auto">
                  Your 3-month first-time investment bonus has expired.
                </p>
              </div>
            ) : (
              <div>
                <div className="absolute top-0 right-0 bg-green-500 text-white px-4 py-1 rounded-bl-xl font-bold text-sm">
                  Active
                </div>
                <FaGift className="text-green-500 text-6xl mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Congratulations!</h2>
                <p className="text-gray-600 mb-8">You received a 25% bonus on your first investment.</p>
                
                <div className="bg-green-50 rounded-xl p-8 border border-green-100 max-w-md mx-auto mb-8 shadow-inner">
                  <p className="text-sm text-green-800 font-semibold uppercase tracking-wider mb-2">Bonus Amount</p>
                  <p className="text-5xl font-bold text-green-600">₹{bonus.amount?.toLocaleString()}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
                  <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                    <FaCalendarAlt className="text-green-500 mt-1 mr-3 text-lg" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-semibold">Granted On</p>
                      <p className="font-medium">{new Date(bonus.givenAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-start bg-gray-50 p-4 rounded-lg">
                    <FaCalendarAlt className="text-orange-500 mt-1 mr-3 text-lg" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-semibold">Expires On</p>
                      <p className="font-medium">{expiryDate?.toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start text-left max-w-2xl mx-auto text-sm">
                  <FaInfoCircle className="mt-0.5 mr-2 flex-shrink-0 text-blue-500" />
                  <p>
                    <strong>Note:</strong> This bonus is a promotional reward and cannot be directly withdrawn to your bank account. It is valid for exactly 3 months from the date of your first investment.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserBonus;
